import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
function slowScrollTo(targetId: string, duration = 1800) {
  const el = document.getElementById(targetId);
  if (!el) return;
  const startY = window.scrollY;
  const targetY = el.getBoundingClientRect().top + startY;
  const distance = targetY - startY;
  const startTime = performance.now();
  function step(now: number) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / duration, 1);
    const eased = easeInOutCubic(t);
    window.scrollTo(0, startY + distance * eased);
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}
type Tomo = {
  number: string;
  title: string;
  range: string;
  desc: string;
  includes: string[];
  tools: string;
};
const tomos: Tomo[] = [{
  number: 'TOMO 1',
  title: 'EL MANUAL DE RESPUESTAS',
  range: '0 – 6 Meses · De la crisis a la estabilidad',
  desc: 'El sueño, los cólicos y la seguridad resueltos. Protocolos claros para los momentos más difíciles del inicio. Dejas de adivinar y empiezas a actuar con certeza.',
  includes: ['Por qué tu bebé no duerme y cómo cambiarlo', 'Gases, cólicos y reflujo: diagnóstico y alivio paso a paso', 'Atragantamiento, caídas y fiebre: qué hacer en cada caso', 'Rutina de sueño de 20 minutos que funciona de verdad', 'Manual de crisis para responder en 30 segundos'],
  tools: 'Registro de Descanso (7 días) · Guía de Alivio Digestivo · Teléfonos y Pasos de Emergencia'
}, {
  number: 'TOMO 2',
  title: 'EL MANUAL DE DESARROLLO',
  range: '6 – 12 Meses · De la incertidumbre al disfrute',
  desc: 'Alimentación, desarrollo y armonía familiar. Cuando superas el caos del inicio y necesitas un mapa para crecer junto a tu bebé con confianza.',
  includes: ['BLW y papillas: cómo introducir sólidos sin miedo', 'Hitos del desarrollo mes a mes y cómo estimularlos', 'Lenguaje, gateo y juego: qué hacer y qué no forzar', 'Carga mental y reparto de tareas sin conflictos', 'Cómo reconectar como pareja sin tiempo ni energía'],
  tools: 'Lista de los 100 Alimentos · Mapa de Avances Mes a Mes · Acuerdo de Pareja'
}];
export function ProductSection() {
  return <section className="bg-[#f4f6fa] py-[72px] px-5" data-id="element-128">
      <div className="max-w-[1000px] mx-auto" data-id="element-129">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center" data-id="element-130">
          <span className="text-[0.75rem] tracking-[0.18em] uppercase text-navy mb-3 block" data-id="element-131">
            El Sistema Completo
          </span>
          <h2 className="font-serif text-navy mb-5 text-[clamp(1.6rem,3.5vw,2.4rem)]" data-id="element-132">
            Dos tomos.{' '}
            <em className="not-italic text-gold-dark" data-id="element-133">Un sistema completo.</em>
          </h2>
          <div className="w-12 h-[3px] bg-gold mx-auto mb-8" data-id="element-134" />
          <p className="max-w-[580px] mx-auto mb-4 text-[#666]" data-id="element-135">
            Cada tomo está diseñado para una etapa específica de tu bebé. Sin
            relleno, sin teoría innecesaria. Solo lo que necesitas saber y las
            herramientas para aplicarlo hoy mismo.
          </p>
        </motion.div>

        <div className="grid gap-6 mt-10 grid-cols-1 md:grid-cols-2" data-id="element-136">
          {tomos.map((t, i) => <motion.article key={t.title} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.15
        }} whileHover={{
          y: -3
        }} className="bg-white rounded-xl p-[30px] shadow-[0_4px_20px_rgba(0,0,0,0.07)] border-t-4 border-navy-light transition-transform" data-id="element-137">
              <span className="tracking-[0.18em] uppercase text-navy mb-2 block text-[12px]" data-id="element-138">
                {t.number}
              </span>
              <h3 className="font-serif text-[1.2rem] text-navy mb-3" data-id="element-139">
                {t.title}
              </h3>
              <span className="inline-block bg-[#e8f0fe] text-navy-light text-[0.72rem] py-[3px] px-2.5 rounded-full mb-3.5 tracking-wider" data-id="element-140">
                {t.range}
              </span>
              <p className="text-[0.92rem] text-[#555] mb-[18px]" data-id="element-141">{t.desc}</p>

              <ul className="list-none" data-id="element-142">
                {t.includes.map(line => <li key={line} className="text-[0.85rem] text-[#444] py-1.5 pl-5 relative" data-id="element-143">
                    <CheckIcon className="absolute left-0 top-2 w-3.5 h-3.5 text-[#2a7a4f]" data-id="element-144" />
                    {line}
                  </li>)}
              </ul>
            </motion.article>)}
        </div>

        {/* Premium Extra Card */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-8 relative overflow-hidden rounded-xl p-8 border-t-4 border-[#c9973a] shadow-[0_10px_40px_rgba(201,151,58,0.15)]" style={{
        background: 'linear-gradient(160deg, #fffbf0 0%, #fff8e8 100%)'
      }} data-id="element-145">
          {/* Badge premium */}

          <div className="max-w-[800px]" data-id="element-146">
            <span className="text-[0.72rem] tracking-[0.18em] uppercase text-[#c9973a] mb-2 block font-bold" data-id="element-147">
              eBook Premium · Incluido
            </span>
            <h3 className="font-serif text-[1.5rem] text-[#0f1e3d] mb-1" data-id="element-148">
              DESARROLLO SIN LÍMITES
            </h3>
            <p className="text-[0.88rem] text-[#7a5f10] italic mb-4" data-id="element-149">
              Cómo despertar la inteligencia de tu bebé desde el primer día (0-2
              años)
            </p>
            <span className="inline-block bg-[#fef3d0] text-[#7a5f10] text-[0.72rem] py-[3px] px-2.5 rounded-full mb-6 tracking-wider font-medium" data-id="element-150">
              0 – 2 Años · eBook Premium
            </span>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-151">
              <ul className="space-y-3" data-id="element-152">
                {[{
                b: 'BLOQUE 1:',
                t: 'El cerebro de tu bebé. Cómo funciona, qué construye su inteligencia y por qué los primeros 2 años son únicos.'
              }, {
                b: 'BLOQUE 2:',
                t: 'Juegos inteligentes para hacer en casa. Tarjetas de imágenes, pre-lectura y números. Todo con lo que tienes en casa.'
              }, {
                b: 'BLOQUE 3:',
                t: 'Tu casa, su mejor escuela. Cómo organizar el entorno para que aprenda solo mientras explora. Sin pantallas.'
              }, {
                b: 'BLOQUE 4:',
                t: 'Hablar dos idiomas. Método práctico para introducir el inglés aunque tú no lo domines perfectamente.'
              }, {
                b: 'BLOQUE 5:',
                t: 'Un niño que sabe concentrarse. Cómo desarrollar la atención y la paciencia desde los primeros meses.'
              }].map((item, idx) => <li key={idx} className="text-[0.85rem] text-[#5c4a0c] leading-relaxed" data-id="element-153">
                    <strong className="text-[#c9973a] mr-1" data-id="element-154">{item.b}</strong>{' '}
                    {item.t}
                  </li>)}
              </ul>

              <div className="flex flex-col justify-center gap-4" data-id="element-155">
                <div className="flex gap-3" data-id="element-156">
                  {[{
                  v: '5',
                  l: 'Bloques'
                }].map((stat, idx) => {})}
                </div>

                <div className="bg-[#fffbf0] border border-[#f0c040]/40 rounded-md p-4 text-[0.82rem] text-[#7a5f10] leading-relaxed" data-id="element-157">
                  <strong className="text-[#c9973a] block mb-1" data-id="element-158">
                    ✨ CONTENIDO PREMIUM INCLUIDO:
                  </strong>
                  Estimulación temprana · Bilingüismo natural · Entorno de
                  aprendizaje · Desarrollo de la atención · Plan de continuidad
                  hasta los 2 años
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-12 text-center" data-id="element-159">
          <a href="#oferta" onClick={e => {
          e.preventDefault();
          slowScrollTo('oferta', 1800);
        }} className="inline-block font-serif font-bold text-navy text-[1.05rem] px-11 py-[18px] rounded-md tracking-wide transition-all duration-200 hover:-translate-y-0.5 cursor-pointer" style={{
          background: 'linear-gradient(135deg, #f0c040 0%, #e8a820 100%)',
          boxShadow: '0 8px 32px rgba(240,192,64,0.35)'
        }} data-id="element-160">
            → QUIERO ENTENDER A MI BEBÉ HOY
          </a>
        </div>
      </div>
    </section>;
}